import getRandomProfile from "@/assets/profile";
import { Storage } from "aws-amplify";
import { useCallback, useEffect, useMemo, useState } from "react";

interface IUploadFile {
    key: string;
    file: any;
    onStart?: () => void;
    onFinish?: (key: string) => void;
    onError?: (error: any) => void;
    contentType?: string;
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
    compressionEnabled?: boolean;
    public?: boolean; // Make the file publicly accessible
}

/**
 * Compresses an image file by resizing and encoding to specified quality
 * @param file - The image file to compress
 * @param maxWidth - Maximum width (default 1200px)
 * @param maxHeight - Maximum height (default 1200px)
 * @param quality - JPEG quality (0-1, default 0.9)
 * @returns Promise resolving to a compressed Blob
 */
export const compressImage = async (
    file: File,
    maxWidth: number = 1200,
    maxHeight: number = 1200,
    quality: number = 0.9
): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        // Skip compression for non-image files
        if (!file.type.startsWith("image/")) {
            resolve(file);
            return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target?.result as string;
            img.onload = () => {
                // Calculate new dimensions while preserving aspect ratio
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > maxWidth) {
                        height = Math.round(height * (maxWidth / width));
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = Math.round(width * (maxHeight / height));
                        height = maxHeight;
                    }
                }

                // Create canvas and draw resized image
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");

                if (!ctx) {
                    reject(new Error("Could not get canvas context"));
                    return;
                }

                ctx.imageSmoothingQuality = "high";
                ctx.drawImage(img, 0, 0, width, height);

                // Convert to blob
                const mimeType = file.type || "image/jpeg";
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error("Canvas to Blob conversion failed"));
                            return;
                        }

                        const compressedFile = new File([blob], file.name, {
                            type: mimeType,
                            lastModified: Date.now(),
                        });

                        console.log(`Image compressed: ${(file.size / 1024).toFixed(2)}KB → ${(blob.size / 1024).toFixed(2)}KB`);
                        resolve(compressedFile);
                    },
                    mimeType,
                    quality
                );
            };
            img.onerror = () => {
                reject(new Error("Error loading image"));
            };
        };
        reader.onerror = () => {
            reject(new Error("Error reading file"));
        };
    });
};

export const uploadFile = async (payload: IUploadFile) => {
    try {
        if (payload.onStart) payload.onStart();

        const dataFile: File = payload.file?.originFileObj || payload.file;
        let fileToUpload: File | Blob = dataFile;

        // Compress image if it's an image file and compression is not explicitly disabled
        if (
            dataFile.type.startsWith("image/") &&
            payload.compressionEnabled !== false
        ) {
            fileToUpload = await compressImage(
                dataFile,
                payload.maxWidth || 1200,
                payload.maxHeight || 1200,
                payload.quality || 0.9
            );
        }

        await Storage.put(payload.key, fileToUpload, {
            contentType: payload.contentType || dataFile?.type,
        });

        if (payload.onFinish) payload.onFinish(payload.key);
    } catch (error) {
        console.error("Error upload file URL:", error);
        if (payload.onError) payload.onError(error);
    }
};

// Enhanced cache with expiration and memory management
interface CacheEntry {
    url: string;
    timestamp: number;
    size: number;
}

const IMAGE_CACHE = new Map<string, CacheEntry>();
const CACHE_EXPIRY_TIME = 30 * 60 * 1000; // 30 minutes
const MAX_CACHE_SIZE = 50; // Maximum number of cached items
const MAX_MEMORY_SIZE = 100 * 1024 * 1024; // 100MB in bytes

// Clean up expired cache entries and manage memory
const cleanupCache = () => {
    const now = Date.now();
    const entries = Array.from(IMAGE_CACHE.entries());

    // Remove expired entries
    for (const [key, entry] of entries) {
        if (now - entry.timestamp > CACHE_EXPIRY_TIME) {
            URL.revokeObjectURL(entry.url); // Prevent memory leaks
            IMAGE_CACHE.delete(key);
        }
    }

    // If still over limits, remove oldest entries
    const remainingEntries = Array.from(IMAGE_CACHE.entries())
        .sort(([, a], [, b]) => a.timestamp - b.timestamp);

    let totalSize = remainingEntries.reduce((sum, [, entry]) => sum + entry.size, 0);

    while (remainingEntries.length > MAX_CACHE_SIZE || totalSize > MAX_MEMORY_SIZE) {
        const [key, entry] = remainingEntries.shift()!;
        URL.revokeObjectURL(entry.url);
        IMAGE_CACHE.delete(key);
        totalSize -= entry.size;
    }
};

// Add a pending requests map to prevent duplicate concurrent requests
const PENDING_REQUESTS = new Map<string, Promise<string>>();

export const getProfilePicture = async (key: string): Promise<string> => {
    // Input validation
    if (!key || typeof key !== 'string') {
        throw new Error('Invalid key provided');
    }

    // Check cache first
    const cachedEntry = IMAGE_CACHE.get(key);
    if (cachedEntry) {
        const now = Date.now();
        if (now - cachedEntry.timestamp < CACHE_EXPIRY_TIME) {
            return cachedEntry.url;
        } else {
            // Expired entry - clean it up
            URL.revokeObjectURL(cachedEntry.url);
            IMAGE_CACHE.delete(key);
        }
    }

    // Check if there's already a pending request for this key
    const pendingRequest = PENDING_REQUESTS.get(key);
    if (pendingRequest) {
        return pendingRequest;
    }

    // Create new request and store it as pending
    const requestPromise = (async () => {
        try {
            const profilePicture = await getDownloadedImageSrc(key);

            // Cache the new entry without additional fetch
            IMAGE_CACHE.set(key, {
                url: profilePicture,
                timestamp: Date.now(),
                size: 1024 // Use estimated size instead of fetching
            });

            // Cleanup cache if needed
            cleanupCache();

            return profilePicture;
        } catch (error) {
            console.error(`Failed to get profile picture for key: ${key}`, error);
            throw error;
        } finally {
            // Remove from pending requests when done
            PENDING_REQUESTS.delete(key);
        }
    })();

    // Store the promise to prevent duplicate requests
    PENDING_REQUESTS.set(key, requestPromise);

    return requestPromise;
};

export async function getDownloadedImageSrc(key: string): Promise<string> {
    try {
        const res = await Storage.get(key, { download: true }) as any;

        let blob: Blob;
        if (res?.Body instanceof Blob) {
            blob = res.Body;
        } else if (res?.Body?.arrayBuffer) {
            const arrayBuffer = await res.Body.arrayBuffer();
            blob = new Blob([arrayBuffer]);
        } else if (res instanceof Blob) {
            blob = res;
        } else {
            throw new Error(`Unexpected Storage.get response for image with key: ${key}`);
        }

        const url = URL.createObjectURL(blob);
        return url;
    } catch (error) {
        console.error(`Error downloading image for key: ${key}`, error);
        throw error;
    }
}

// Optional: Add cleanup function for component unmount
export const clearImageCache = () => {
    Array.from(IMAGE_CACHE.entries()).forEach(([key, entry]) => {
        URL.revokeObjectURL(entry.url);
    });
    IMAGE_CACHE.clear();
};

export const getFileURLWithCache = async (
    key: string,
): Promise<string | null> => {
    try {
        return getProfilePicture(key);
    } catch (error) {
        console.error("Error fetching file URL:", error);
        return null; // or handle the error in a way that makes sense for your application
    }
};

export const getFileURL = async (key: string): Promise<string | null> => {
    try {
        const downloadedUrl = await getProfilePicture(key);
        return downloadedUrl;
    } catch (error) {
        console.error("Error fetching file URL:", error);
        return null; // or handle the error in a way that makes sense for your application
    }
};

export const getFileURLWithSize = async (key: string): Promise<{
    url: string;
    size: number | null;
} | null> => {
    try {
        const downloadedUrl = await getProfilePicture(key);
        return {
            url: downloadedUrl,
            size: Math.round(Number((await Storage.getProperties(key)).contentLength) / 1024 / 1024)
        }
    } catch (error) {
        console.error("Error fetching file URL:", error);
        return null; // or handle the error in a way that makes sense for your application
    }
};

export const useGetFileURL = (path: string, withSize?: boolean) => {
    const [url, setUrl] = useState<string>("");
    const [keyUrl, setKeyUrl] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false); // Start with false
    const [isError, setIsError] = useState(false);
    const [fileSize, setFileSize] = useState<number | null>(null);

    // Stabilize the callback - only update URL, don't change loading state here
    const setLoadingAndUrl = useCallback((newUrl: string) => {
        setUrl(prevUrl => {
            if (newUrl !== prevUrl) {
                // Remove setIsLoading(false) from here
                return newUrl;
            }
            return prevUrl;
        });
    }, []);

    // Memoize the fetch function
    const getFileUrlIfNeeded = useCallback(async (key: string) => {
        if (
            typeof key === "string" &&
            (key.startsWith("http://") || key.startsWith("https://"))
        ) {
            setLoadingAndUrl(key);
            setIsLoading(false); // Set loading false for direct URLs
            return;
        }

        if (key && key !== keyUrl) {
            try {
                setIsError(false);
                setIsLoading(true);

                if (withSize) {
                    const urlResWSize = await getFileURLWithSize(key);
                    if (urlResWSize?.url) {
                        setLoadingAndUrl(urlResWSize.url);
                        setFileSize(urlResWSize.size || null);
                        setIsLoading(false); // Only set loading false after successful fetch
                    }
                } else {
                    const urlRes = await getFileURL(key);
                    if (urlRes) {
                        setLoadingAndUrl(urlRes);
                        setIsLoading(false); // Only set loading false after successful fetch
                    }
                }
                setKeyUrl(key);
            } catch (error) {
                console.error("Error getFileURL file URL:", error);
                setIsError(true);
                setIsLoading(false);
            }
        }
    }, [keyUrl, withSize, setLoadingAndUrl]);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            if (!path) {
                if (isMounted) {
                    setIsLoading(false);
                }
                return;
            }



            // Avoid unnecessary loading state changes
            if (path === keyUrl && url) {
                if (isMounted) {
                    setIsLoading(false);
                }
                return;
            }
            try {
                if (!path.includes("/")) {
                    let str = getRandomProfile(path)
                    await getFileUrlIfNeeded(str)
                } else {
                    await getFileUrlIfNeeded(path);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                if (isMounted) {
                    setIsError(true);
                    setIsLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [path, getFileUrlIfNeeded, keyUrl, url]);

    return { isLoading, url, isError, getFileUrlIfNeeded, setUrl, fileSize };
};